import React, { useContext, useEffect, useState } from 'react';
import $ from 'jquery';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Box, Button, Stack, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toast from '../../../../components/Toast';
import { useAuth } from '../../../../hooks/Auth';
import { useIntegration } from '../../../../hooks/Integration';
import { useModal } from '../../../../hooks/Modal';
import { options } from '../../../../helpers/toastOptions';
import { parseStringWithRegex } from '../../../../helpers/parseString';
import { parseCpfCnpj, parsePhone } from '../../../../helpers/parseValues';
import { DefaultCollectContext } from '../..';
import { ModalTitle } from './styled';

export const ModalAddCollectAddress = () => {
  const [senderDocument, setSenderDocument] = useState('');
  const [senderZipPostalCode, setSenderZipPostalCode] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderAddressNumber, setSenderAddressNumber] = useState('');
  const [senderAdditionalInformation, setSenderAdditionalInformation] =
    useState('');
  const [senderSuburb, setSenderSuburb] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderState, setSenderState] = useState('');
  // const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');
  const [senderContactNumber, setSenderContactNumber] = useState('');
  const [hasInvalidZipCode, setHasInvalidZipCode] = useState(false);
  const [disableName, setDisableName] = useState(false);
  const [disableStreetAddress, setDisableStreetAddress] = useState(true);
  const [disableSuburb, setDisableSuburb] = useState(true);
  const [isToValidateDocument, setIsToValidateDocument] = useState(true);
  const [isToValidateAddressNumber, setIsToValidateAddressNumber] =
    useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');

  const { token } = useAuth();
  const { searchUser, getAddressByZipPostalCode } = useIntegration();
  const { Modal, closeModal } = useModal();

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const {
    addressList,
    setAddressList,
    setSelectedAddressIndex,
    setIsToValidateSenderAddressNumber,
    openChangeCollectAddressModal,
    dataToAddCollectAddressModal,
    setDataToAddCollectAddressModal
  } = useContext(DefaultCollectContext);

  const addNewAddressSchema = Yup.object().shape({
    document: Yup.string()
      .required('CNPJ/CPF obrigatório')
      .trim('CNPJ/CPF obrigatório')
      .test('validadeDocument', 'CNPJ/CPF inválido', (value) => {
        if (isToValidateDocument) {
          return (
            typeof value === 'string' &&
            [11, 14].includes(parseCpfCnpj(value)?.length)
          );
        }

        return true;
      }),
    name: Yup.string()
      .required('Razão Social / Nome obrigatório')
      .trim('Razão Social / Nome obrigatório'),
    zipPostalCode: Yup.string()
      .required('CEP obrigatório')
      .trim('CEP obrigatório')
      .test('validateZipPostalCode', 'CEP inválido', (value) => {
        const regex = /^\d{8}$/;
        return (
          value && value.replace('-', '').match(regex) && !hasInvalidZipCode
        );
      }),
    streetAddress: Yup.string()
      .required('Endereço obrigatório')
      .trim('Endereço obrigatório'),
    streetAddressNumber: Yup.string()
      .required('Número obrigatório')
      .trim('Número obrigatório')
      .test('validateStreetAddressNumber', 'Número inválido', (value) => {
        if (!isToValidateAddressNumber) {
          return true;
        }

        return value && value !== '0';
      }),
    additionalInformation: Yup.string().nullable(),
    suburb: Yup.string()
      .required('Bairro obrigatório')
      .trim('Bairro obrigatório'),
    city: Yup.string()
      .required('Cidade obrigatória')
      .trim('Cidade obrigatória'),
    state: Yup.string()
      .required('Estado obrigatório')
      .trim('Estado obrigatório'),
    contactName: Yup.string()
      .required('Nome obrigatório')
      .trim('Nome obrigatório'),
    contactEmail: Yup.string()
      .required('E-mail obrigatório')
      .trim('E-mail obrigatório')
      .matches(/^([\w.-]+)@([\w-]+)((\.(\w){2,25})+)$/, 'E-mail inválido'),
    contactNumber: Yup.string()
      .required('Telefone obrigatório')
      .trim('Telefone obrigatório')
      .test('validatePhoneNumber', 'Telefone inválido', (value) => {
        return parsePhone(value).length >= 10;
      }),
    contactExtension: Yup.string()
  });

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(addNewAddressSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = async ({
    document,
    name,
    zipPostalCode,
    streetAddress,
    streetAddressNumber,
    additionalInformation,
    suburb,
    city,
    state,
    contactName,
    contactEmail,
    contactNumber,
    contactExtension
  }) => {
    try {
      setIsSubmitting(true);

      const getAddressResponse = await getAddressByZipPostalCode({
        zipPostalCode: parseCpfCnpj(zipPostalCode)
      });

      if (!getAddressResponse) {
        setError('zipPostalCode', {
          message: 'CEP inválido'
        });

        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      setIsSubmitting(false);
    }

    const indexAlreadyExists = addressList.findIndex(
      (value) =>
        name?.toLowerCase() === value.name?.toLowerCase() &&
        document === value.document &&
        additionalInformation?.toLowerCase() ===
          value.additionalInformation?.toLowerCase() &&
        city?.toLowerCase() === value.city?.toLowerCase() &&
        state?.toLowerCase() === value.state?.toLowerCase() &&
        streetAddress?.toLowerCase() === value.streetAddress?.toLowerCase() &&
        streetAddressNumber?.toLowerCase() ===
          value.streetAddressNumber?.toLowerCase() &&
        suburb?.toLowerCase() === value.suburb?.toLowerCase() &&
        parsePhone(zipPostalCode) === value.zipPostalCode &&
        contactEmail?.toLowerCase() === value.contactEmail?.toLowerCase() &&
        contactExtension?.toLowerCase() ===
          value.contactExtension?.toLowerCase() &&
        contactName?.toLowerCase() === value.contactName?.toLowerCase() &&
        parsePhone(contactNumber)?.toLowerCase() ===
          value.contactNumber?.toLowerCase()
    );

    if (indexAlreadyExists < 0) {
      const olderLength = addressList?.length;

      setAddressList([
        ...addressList,
        {
          document,
          name,
          zipPostalCode: parsePhone(zipPostalCode),
          streetAddress,
          streetAddressNumber,
          additionalInformation,
          suburb,
          city,
          state,
          contactName,
          contactEmail,
          contactNumber: parsePhone(contactNumber),
          contactExtension
        }
      ]);

      setSelectedAddressIndex(olderLength);
    } else {
      setSelectedAddressIndex(indexAlreadyExists);
    }

    setDataToAddCollectAddressModal({});
    setIsToValidateSenderAddressNumber(false);
    closeModal();
  };

  const getDocumentInfo = async (value) => {
    setIsToValidateDocument(true);
    setIsToValidateAddressNumber(true);

    if (value) {
      try {
        const localToken = localStorage.getItem('@Braspress:token');

        const response = await searchUser({
          searchData: { document: parseCpfCnpj(value) },
          token: localToken
        });

        if (response?.length > 0) {
          const userSearched = response[0];

          if (userSearched?.document) {
            const {
              name,
              zipPostalCode,
              streetAddress,
              streetAddressNumber,
              addressComplement,
              suburb,
              city,
              state
            } = userSearched;

            setValue('name', name.toUpperCase());
            setSenderName(name.toUpperCase());

            setValue('zipPostalCode', zipPostalCode);
            setSenderZipPostalCode(zipPostalCode);
            setZipSearchedByDocument(zipPostalCode);

            const newStringAddress = parseStringWithRegex({
              string: String(streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });

            setSenderAddress(newStringAddress);
            setValue('streetAddress', newStringAddress);
            // newStringAddress && clear.push('streetAddress');

            // setValue('streetAddress', streetAddress.toUpperCase());
            // setSenderAddress(streetAddress.toUpperCase());

            setValue('streetAddressNumber', streetAddressNumber);
            setSenderAddressNumber(streetAddressNumber);

            if (addressComplement) {
              const newStringAddressComplement = parseStringWithRegex({
                string: String(addressComplement).trim(),
                regex: /^[aA-zZ0-9\s]+$/
              });

              setSenderAdditionalInformation(newStringAddressComplement);
              setValue('additionalInformation', newStringAddressComplement);
            } else {
              setValue('additionalInformation', '');
              setSenderAdditionalInformation('');
            }

            if (suburb) {
              const newStringSuburb = parseStringWithRegex({
                string: String(suburb).trim(),
                regex: /^[aA-zZ\s]+$/
              });

              setSenderSuburb(newStringSuburb.toUpperCase());
              setValue('suburb', newStringSuburb.toUpperCase());
              setDisableSuburb(true);
            } else {
              setDisableSuburb(false);
              setValue('suburb', '');
              setSenderSuburb('');
            }

            // newStringSuburb && clear.push('suburb');

            // setValue('suburb', suburb.toUpperCase());
            // setSenderSuburb(suburb.toUpperCase());

            setValue('city', city.toUpperCase());
            setSenderCity(city.toUpperCase());

            setValue('state', state.toUpperCase());
            setSenderState(state.toUpperCase());

            setHasInvalidZipCode(false);
            setDisableName(true);
            setDisableStreetAddress(true);
            // setDisableSuburb(true);
            setIsToValidateDocument(false);

            clearErrors([
              'document',
              'name',
              'zipPostalCode',
              'streetAddress',
              'streetAddressNumber',
              'additionalInformation',
              'suburb',
              'city',
              'state'
            ]);
          }
        } else {
          setDisableName(false);

          toast(
            <Toast
              title="Atenção!"
              content="Usuário não foi localizado com o documento informado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableName(false);
      setValue('name', '');
      setSenderName('');

      if (!senderZipPostalCode) {
        setValue('streetAddress', '');
        setSenderAddress('');
        setValue('streetAddressNumber', '');
        setSenderAddressNumber('');
        setValue('additionalInformation', '');
        setSenderAdditionalInformation('');
        setValue('suburb', '');
        setSenderSuburb('');
        setValue('city', '');
        setSenderCity('');
        setValue('state', '');
        setSenderState('');
      }
    }
  };

  const searchAddressByZipPostalCode = async (value) => {
    const regex = /^\d{8}$/;

    if (value === zipSearchedByDocument) {
      return;
    }
    if (value.replace('-', '').match(regex)) {
      // if (value.replace('-', '') === zipSearchedByDocument) {
      //   return;
      // }

      try {
        const data = await getAddressByZipPostalCode({
          zipPostalCode: value.replace('-', ''),
          token
        });

        if (data) {
          const clear = ['zipPostalCode'];

          setHasInvalidZipCode(false);

          if (data.streetAddress) {
            const newStringAddress = parseStringWithRegex({
              string: String(data.streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });

            if (newStringAddress) {
              setSenderAddress(newStringAddress?.toUpperCase());
              setValue('streetAddress', newStringAddress?.toUpperCase());
              clear.push('streetAddress');
              setDisableStreetAddress(true);
            } else {
              setSenderAddress('');
              setValue('streetAddress', '');
              setDisableStreetAddress(false);
            }
          } else {
            setValue('streetAddress', '');
            setSenderAddress('');
            setDisableStreetAddress(false);
          }

          if (data.suburb) {
            const newStringSuburb = parseStringWithRegex({
              string: String(data.suburb).trim(),
              regex: /^[aA-zZ\s]+$/
            });

            if (newStringSuburb) {
              setSenderSuburb(newStringSuburb?.toUpperCase());
              setValue('suburb', newStringSuburb?.toUpperCase());
              clear.push('suburb');
              setDisableSuburb(true);
            } else {
              setSenderSuburb('');
              setValue('suburb', '');
              setDisableSuburb(false);
            }
          } else {
            setValue('suburb', '');
            setSenderSuburb('');
            setDisableSuburb(false);
          }

          if (data.city) {
            setValue('city', data.city.toUpperCase());
            setSenderCity(data.city.toUpperCase());
            clear.push('city');
          } else {
            setValue('city', '');
            setSenderCity('');
          }

          if (data.state) {
            setValue('state', data.state.toUpperCase());
            setSenderState(data.state.toUpperCase());
            clear.push('state');
          } else {
            setValue('state', '');
            setSenderState('');
          }

          setValue('streetAddressNumber', '');
          setSenderAddressNumber('');
          setValue('additionalInformation', '');
          setSenderAdditionalInformation('');

          clearErrors(clear);
        } else {
          setError('zipPostalCode', {
            message: 'CEP inválido'
          });
          setHasInvalidZipCode(true);
          setDisableStreetAddress(true);
          setDisableSuburb(true);
          setValue('streetAddress', '');
          setSenderAddress('');
          setValue('streetAddressNumber', '');
          setSenderAddressNumber('');
          setValue('additionalInformation', '');
          setSenderAdditionalInformation('');
          setValue('suburb', '');
          setSenderSuburb('');
          setValue('city', '');
          setSenderCity('');
          setValue('state', '');
          setSenderState('');

          toast(
            <Toast
              title="Atenção!"
              content="O CEP informado não foi localizado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableStreetAddress(true);
      setDisableSuburb(true);
      setValue('streetAddress', '');
      setSenderAddress('');
      setValue('streetAddressNumber', '');
      setSenderAddressNumber('');
      setValue('additionalInformation', '');
      setSenderAdditionalInformation('');
      setValue('suburb', '');
      setSenderSuburb('');
      setValue('city', '');
      setSenderCity('');
      setValue('state', '');
      setSenderState('');
    }
  };

  useEffect(() => {
    const cpfCnpjOptions = {
      onKeyPress(cpf, _ev, _el, op) {
        const masks = ['000.000.000-000', '00.000.000/0000-00'];
        $('#document').mask(cpf.length > 14 ? masks[1] : masks[0], op);
      }
    };

    $('#document').length > 11
      ? $('#document').mask('00.000.000/0000-00', cpfCnpjOptions)
      : $('#document').mask('000.000.000-00#', cpfCnpjOptions);
  }, [senderDocument]);

  useEffect(() => {
    $('#zipPostalCode').mask('00000-000');
  }, [senderZipPostalCode]);

  useEffect(() => {
    $('#contactNumber').mask('(00) 0000-00009');
  }, [senderContactNumber]);

  useEffect(() => {
    if (dataToAddCollectAddressModal?.document) {
      setValue('document', dataToAddCollectAddressModal.document);
      setSenderDocument(dataToAddCollectAddressModal.document);

      setValue('name', dataToAddCollectAddressModal.name);
      setSenderName(dataToAddCollectAddressModal.name);

      setValue('zipPostalCode', dataToAddCollectAddressModal.zipPostalCode);
      setSenderZipPostalCode(dataToAddCollectAddressModal.zipPostalCode);

      if (dataToAddCollectAddressModal.streetAddress) {
        const newStringAddress = parseStringWithRegex({
          string: String(dataToAddCollectAddressModal.streetAddress).trim(),
          regex: /^[aA-zZ0-9\s]+$/
        });
        setSenderAddress(newStringAddress.toUpperCase());
        setValue('streetAddress', newStringAddress.toUpperCase());
      } else {
        setValue('streetAddress', '');
        setSenderAddress('');
        setDisableStreetAddress(false);
      }

      // setValue('streetAddress', dataToAddCollectAddressModal.streetAddress);
      // setSenderAddress(dataToAddCollectAddressModal.streetAddress);

      setValue(
        'streetAddressNumber',
        dataToAddCollectAddressModal.streetAddressNumber
      );
      setSenderAddressNumber(dataToAddCollectAddressModal.streetAddressNumber);

      if (dataToAddCollectAddressModal.additionalInformation) {
        const newStringAddressComplement = parseStringWithRegex({
          // eslint-disable-next-line prettier/prettier
          string: String(dataToAddCollectAddressModal.additionalInformation).trim(),
          regex: /^[aA-zZ0-9\s]+$/
        });
        setValue(
          'additionalInformation',
          newStringAddressComplement.toUpperCase()
        );
        setSenderAdditionalInformation(
          newStringAddressComplement.toUpperCase()
        );
      } else {
        setValue('additionalInformation', '');
        setSenderAdditionalInformation('');
      }
      // setValue(
      //   'additionalInformation',
      //   dataToAddCollectAddressModal.additionalInformation
      // );
      // setSenderAdditionalInformation(
      //   dataToAddCollectAddressModal.additionalInformation
      // );

      if (dataToAddCollectAddressModal.suburb) {
        const newStringSuburb = parseStringWithRegex({
          string: String(dataToAddCollectAddressModal.suburb).trim(),
          regex: /^[aA-zZ\s]+$/
        });

        setSenderSuburb(newStringSuburb.toUpperCase());
        setValue('suburb', newStringSuburb.toUpperCase());
        if (
          !dataToAddCollectAddressModal.suburb.match(/^[aA-zZ\s]+$/) &&
          dataToAddCollectAddressModal.suburb !== ''
        ) {
          setDisableSuburb(false);
        }
      } else {
        setDisableSuburb(false);
        setValue('suburb', '');
        setSenderSuburb('');
      }
      // setValue('suburb', dataToAddCollectAddressModal.suburb);
      // setSenderSuburb(dataToAddCollectAddressModal.suburb);

      setValue('city', dataToAddCollectAddressModal.city);
      setSenderCity(dataToAddCollectAddressModal.city);

      setValue('state', dataToAddCollectAddressModal.state);
      setSenderState(dataToAddCollectAddressModal.state);

      setValue('contactName', dataToAddCollectAddressModal.contactName);

      setValue('contactEmail', dataToAddCollectAddressModal.contactEmail);

      setValue('contactNumber', dataToAddCollectAddressModal.contactNumber);
      setSenderContactNumber(dataToAddCollectAddressModal.contactNumber);

      setValue(
        'contactExtension',
        dataToAddCollectAddressModal.contactExtension
      );

      setIsToValidateDocument(false);

      if (dataToAddCollectAddressModal.hasInvalidZipCode) {
        setHasInvalidZipCode(true);
        setError('zipPostalCode', {
          message: 'CEP inválido'
        });
      }

      if (dataToAddCollectAddressModal?.hasInvalidStreetAddressNumber) {
        setError('streetAddressNumber', {
          message: 'Número inválido'
        });
      }
    }
  }, []);

  return (
    <Modal preventClose>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxHeight: '86vh',
            overflow: 'auto'
          }}
        >
          <ModalTitle>ADICIONAR NOVO ENDEREÇO</ModalTitle>

          <Stack spacing={2} style={{ marginTop: '8px' }}>
            <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
              <Controller
                name="document"
                control={control}
                defaultValue=""
                render={({ field }) => {
                  return (
                    <TextField
                      label="CNPJ / CPF *"
                      id="document"
                      data-testid="newCollectAddressDocumentInput"
                      style={{ minWidth: 240 }}
                      {...field}
                      value={(field.value = senderDocument)}
                      disabled={isSubmitting}
                      onChange={(e) => {
                        setIsToValidateDocument(true);
                        field.onChange(e.target.value);
                        setSenderDocument(e.target.value);
                      }}
                      onBlur={(e) => {
                        getDocumentInfo(e.target.value);
                        field.onBlur(field.onChange(e.target.value));
                        setSenderDocument(e.target.value);
                      }}
                      error={!!errors.document}
                      helperText={errors.document?.message}
                    />
                  );
                }}
              />

              <Controller
                name="name"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Razão Social/Nome *"
                    data-testid="newCollectAddressNameInput"
                    style={{ width: '100%' }}
                    {...field}
                    value={(field.value = senderName)}
                    disabled={disableName || isSubmitting}
                    onChange={(e) => {
                      const regex = /^[aA-zZ0-9\s]+$/;
                      if (
                        !e.target.value.match(regex) &&
                        e.target.value !== ''
                      ) {
                        return;
                      }
                      field.onChange(e.target.value.toUpperCase());
                      setSenderName(e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                      setSenderName(e.target.value.toUpperCase());
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Stack>

            <Stack direction={maxSM ? 'column' : 'row'} spacing={2}>
              <Controller
                name="zipPostalCode"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="CEP *"
                    id="zipPostalCode"
                    data-testid="newCollectAddressZipPostalCodeInput"
                    style={{ width: !maxMD && 240 }}
                    {...field}
                    value={(field.value = senderZipPostalCode)}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setHasInvalidZipCode(true);
                      field.onChange(e.target.value);
                      setSenderZipPostalCode(e.target.value);
                      searchAddressByZipPostalCode(e.target.value);
                    }}
                    error={!!errors.zipPostalCode}
                    helperText={errors.zipPostalCode?.message}
                    onBlur={(e) => {
                      const { value } = e.target;
                      searchAddressByZipPostalCode(value);

                      field.onBlur(field.onChange(value));
                      setSenderZipPostalCode(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="streetAddress"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Endereço *"
                    data-testid="newCollectAddressStreetAddressInput"
                    style={{ width: maxMD && '100%', flex: !maxMD && '1' }}
                    {...field}
                    value={(field.value = senderAddress)}
                    disabled={disableStreetAddress || isSubmitting}
                    onChange={(e) => {
                      const regex = /^[aA-zZ0-9\s]+$/;
                      if (
                        !e.target.value.match(regex) &&
                        e.target.value !== ''
                      ) {
                        return;
                      }
                      field.onChange(e.target.value.toUpperCase());
                      setSenderAddress(e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                      setSenderAddress(e.target.value.toUpperCase());
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.streetAddress}
                    helperText={errors.streetAddress?.message}
                  />
                )}
              />

              <Controller
                name="streetAddressNumber"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Número *"
                    data-testid="newCollectAddressStreetAddressNumberInput"
                    style={{ width: maxSM ? '100%' : 96, minWidth: 96 }}
                    {...field}
                    value={(field.value = senderAddressNumber)}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const regex = /^[0-9]+$/;
                      if (
                        !e.target.value.match(regex) &&
                        e.target.value !== ''
                      ) {
                        return;
                      }

                      setIsToValidateAddressNumber(false);
                      field.onChange(e.target.value);
                      setSenderAddressNumber(e.target.value);
                    }}
                    onBlur={(e) => {
                      field.onBlur(field.onChange(e.target.value));
                      setSenderAddressNumber(e.target.value);
                    }}
                    inputProps={{ maxLength: 50 }}
                    error={!!errors.streetAddressNumber}
                    helperText={errors.streetAddressNumber?.message}
                  />
                )}
              />
            </Stack>

            <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
              <Stack
                direction={maxSM ? 'column' : 'row'}
                spacing={2}
                style={{ width: maxMD ? '100%' : '55%' }}
              >
                <Controller
                  name="additionalInformation"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Complemento"
                      data-testid="newCollectAddressAdditionalInformationInput"
                      style={{ width: !maxMD && 240 }}
                      fullWidth={maxMD}
                      {...field}
                      value={(field.value = senderAdditionalInformation)}
                      disabled={isSubmitting}
                      onChange={(e) => {
                        const regex = /^[aA-zZ0-9\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderAdditionalInformation(
                          e.target.value.toUpperCase()
                        );
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderAdditionalInformation(
                          e.target.value.toUpperCase()
                        );
                      }}
                      inputProps={{ maxLength: 256 }}
                      error={!!errors.additionalInformation}
                      helperText={errors.additionalInformation?.message}
                    />
                  )}
                />

                <Controller
                  name="suburb"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Bairro *"
                      data-testid="newCollectAddressSuburbInput"
                      style={{ flex: !maxMD && '1' }}
                      fullWidth={maxMD}
                      {...field}
                      value={(field.value = senderSuburb)}
                      disabled={disableSuburb || isSubmitting}
                      onChange={(e) => {
                        const regex = /^[aA-zZ\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderSuburb(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderSuburb(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.suburb}
                      helperText={errors.suburb?.message}
                    />
                  )}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                style={{ width: maxMD ? '100%' : '45%' }}
              >
                <Controller
                  name="city"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Cidade *"
                      data-testid="newCollectAddressCityInput"
                      style={{ flex: !maxMD && '1' }}
                      fullWidth={maxMD}
                      {...field}
                      value={(field.value = senderCity)}
                      disabled
                      onChange={(e) => {
                        const regex = /^[aA-zZ\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderCity(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderCity(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />

                <Controller
                  name="state"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="UF *"
                      data-testid="newCollectAddressStateInput"
                      style={{ width: 96, minWidth: 96 }}
                      {...field}
                      value={(field.value = senderState)}
                      disabled
                      onChange={(e) => {
                        const regex = /^[aA-zZ\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderState(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderState(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 2 }}
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>

          <strong style={{ marginTop: '8px' }}>Contato:</strong>
          <Stack spacing={2}>
            <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
              <Controller
                name="contactName"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Nome *"
                    data-testid="newCollectAddressContactNameInput"
                    fullWidth
                    {...field}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const { value } = e.target;
                      const regex = /^[aA-zZ\s]+$/;

                      if (!value.match(regex) && value !== '') {
                        return;
                      }

                      field.onChange(value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                  />
                )}
              />

              <Controller
                name="contactEmail"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="E-mail *"
                    data-testid="newCollectAddressContactEmailInput"
                    fullWidth
                    {...field}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toLowerCase())
                      );
                    }}
                    inputProps={{ maxLength: 50 }}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail?.message}
                  />
                )}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <Controller
                name="contactNumber"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Telefone *"
                    id="contactNumber"
                    data-testid="newCollectAddressContactPhoneInput"
                    {...field}
                    value={(field.value = senderContactNumber)}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setSenderContactNumber(e.target.value);
                      field.onChange(e.target.value);
                    }}
                    onBlur={(e) => {
                      if (e.target.value.length > 14) {
                        $('#contactNumber').mask('(00) 00000-0009');
                      } else {
                        $('#contactNumber').mask('(00) 0000-00009');
                      }

                      setSenderContactNumber(e.target.value);
                      field.onBlur(field.onChange(e.target.value));
                    }}
                    inputProps={{ maxLength: 50 }}
                    error={!!errors.contactNumber}
                    helperText={errors.contactNumber?.message}
                  />
                )}
              />

              <Controller
                name="contactExtension"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Ramal"
                    data-testid="newCollectAddressContactExtensionInput"
                    style={{ width: '120px' }}
                    {...field}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const regex = /^[0-9]+$/;
                      if (
                        !e.target.value.match(regex) &&
                        e.target.value !== ''
                      ) {
                        return;
                      }

                      field.onChange(e.target.value);
                    }}
                    onBlur={(e) => field.onBlur(field.onChange(e.target.value))}
                    inputProps={{ maxLength: 50 }}
                    error={!!errors.contactExtension}
                    helperText={errors.contactExtension?.message}
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} style={{ marginTop: '8px' }}>
            <Box
              style={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                data-testid="closeAddCollectAddressModalButton"
                onClick={() => {
                  setDataToAddCollectAddressModal({});
                  openChangeCollectAddressModal();
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </Box>

            <Box style={{ flex: 1 }}>
              <LoadingButton
                type="submit"
                data-testid="SaveAddCollectAddressModalButton"
                variant="contained"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Salvar
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
      </form>
    </Modal>
  );
};
